import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from './Ipucu';

/**
 * TELEGRAM — panel tarafı (docs/TELEGRAM.md).
 *
 *  · `TelegramBenim`  : herkesin kendi bağlantısı ve bildirim tercihleri (ayarlar yetkisi GEREKMEZ;
 *                       `/telegram` sayfasında ve Ayarlar › Telegram'ın üstünde aynı bileşen).
 *  · `TelegramYonetim`: sağlık, bot ve asistan durumu, özellik bayrakları (kuru çalıştırma dahil), otomasyon
 *                       kuralları, kim bağlı, en çok takılınan konular, izin matrisi (salt okunur),
 *                       son Telegram işlemleri. Yalnız ayarları görebilen role.
 *
 * Sır gösterilmez: bot token'ı ve asistan anahtarı için yalnız "var / yok".
 */
type Tercih = { kapali: string[]; gunluk: boolean; haftalik: boolean; saat: string; sessizBas: string; sessizBit: string };
type Bot = { tokenVar: boolean; acik: boolean; kullaniciAdi: string | null; hata: string | null; at: string | null };
type Susturma = 'bugun' | 'hic' | null;
type Durtme = { kod: string; ad: string; aciklama: string; esik: number | null; birim: string | null; susturma: Susturma };
type Durum = {
  bot: Bot; asistan: { kurulu: boolean; model: string | null }; ben: { bagli: boolean }; tercih: Tercih;
  turler: { id: string; ad: string; acil: boolean }[]; kullanim: { bugun: number; tavan: number };
  durtmeler?: Durtme[];
};
type Kod = { kod: string; komut: string; botVar: boolean; baglanti: string | null; dakika: number };
type Yonetim = {
  bot: Bot; asistan: { kurulu: boolean; model: string | null }; bayrak: { asistan: boolean; yazma: boolean; otomatik: boolean; kuru: boolean }; kuyruk: number;
  kuyrukAyrinti?: { bekleyen: number; gonderiliyor: number; enEskiSn: number | null; hata24: number; iptal24: number; giden24: number; ertelenen: number };
  tavan: { gunlukIleti: number; aracTuru: number };
  kullanicilar: { id: number; name: string; username: string; roleLabel: string; active: boolean; bagli: boolean; ulasilamiyor?: boolean; bugunIleti: number; yediGun: { ileti: number; yapilan: number; hata: number; vazgecilen: number } }[];
  matris: { yazma: { ad: string; sinif: string; metot: string; yol: string }[]; okumaAdedi: number; yasak: { ad: string; neden: string }[] };
  islemler: { id: number; at: string; kullanici: string; action: string; ad: string | null; durum: string | null; hata: string | null; geriAlma: boolean; geriAlindi: boolean }[];
};

type Saglik = {
  bot: { tokenVar: boolean; acik: boolean; kullaniciAdi: string | null; hata: string | null };
  sahip: { surec: string; kalpAt: string; kalpSn: number; canli: boolean; devir: number } | null;
  sonGuncellemeAt: string | null;
  kuyruk: { bekleyen: number; gonderiliyor: number; enEskiSn: number | null; hata24: number; iptal24: number; giden24: number; ertelenen: number; esik: number };
  islem: { calisan: number; belirsiz24: number; hata24: number };
  kesici: { acik: boolean; bugunAcilma: number };
  ulasilamayan: { id: number; ad: string; at: string }[];
  bugun: {
    guncelleme: number; asistanTuru: number; aracCagrisi: number; asistanHata: number; onerilen: number; onaylanan: number; vazgecilen: number;
    suresiDolan: number; basarisiz: number; geriAlma: number; belirsiz: number; gidenGitti: number; gidenHata: number; giden429: number;
    engelleyen: number; kuru: number; takilma: number; dosyaRed: number;
    saglayiciP50: number | null; saglayiciP95: number | null; islemP50: number | null; islemP95: number | null;
  };
  maliyet: { asistanTuru: number; tokenGirdi: number; tokenCikti: number };
  uyarilar: string[]; kalpGecikti: boolean;
};
type KuralTur = 'OLAY' | 'OZET' | 'DURTME' | 'GENEL';
type Kural = {
  kod: string; ad: string; aciklama: string; tur: KuralTur; acik: boolean; esik: number | null;
  birim: 'dakika' | 'saat' | 'is_saati' | 'gun' | 'adet' | null; alici: string; isBas: string; isBit: string; sonTetikAt: string | null; bugun: number;
};
type KuruSatir = { id: string; ad: string; tur: string; sinif: string; at: string };
type Takilma = { konular: { konu: string; adet: number; son: string; kisiler: { ad: string; adet: number }[] }[]; etiketler: string[] };

const BIRIM_AD: Record<string, string> = { dakika: 'dakika', saat: 'saat', is_saati: 'iş saati', gun: 'gün', adet: 'adet' };
const KURAL_GRUPLARI: { tur: KuralTur; ad: string; kapali?: boolean }[] = [
  { tur: 'GENEL', ad: 'Genel' }, { tur: 'OZET', ad: 'Özetler' }, { tur: 'DURTME', ad: 'Dürtmeler' }, { tur: 'OLAY', ad: 'Olay bildirimleri', kapali: true },
];
const sayi = (n: number) => new Intl.NumberFormat('tr-TR').format(n);
/** "12 sn önce" · "5 dk önce" · "3 sa önce" · "2 gün önce". */
const once = (sn: number) => {
  const s = Math.max(0, Math.round(sn));
  if (s < 60) return `${s} sn önce`;
  if (s < 3600) return `${Math.round(s / 60)} dk önce`;
  if (s < 86400) return `${Math.round(s / 3600)} sa önce`;
  return `${Math.round(s / 86400)} gün önce`;
};
/** Gecikme kaba kovalardan gelir: değer kovanın üst sınırıdır (saniye). */
const gecikme = (p50: number | null, p95: number | null) =>
  (p50 === null || p95 === null ? 'ölçüm yok' : `yarısı ≤${p50} sn · %95'i ≤${p95} sn`);

const Hata = ({ m }: { m: string }) => <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{m}</span></div>;
const hataMetni = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);

/** Bildirim türlerinin ekrandaki grubu (sıra da buradan gelir). Listede olmayan tür "Diğer"e düşer. */
const TUR_GRUPLARI = ['Satış', 'Tahsilat ve finans', 'Stok ve üretim', 'Sistem', 'Diğer'] as const;
const TUR_GRUBU: Record<string, (typeof TUR_GRUPLARI)[number]> = {
  TEKLIF_ONAY: 'Satış', TEKLIF_ONAYLANDI: 'Satış', SIPARIS_YENI: 'Satış', TAKIPSIZ_CARI: 'Satış',
  TAHSILAT: 'Tahsilat ve finans', TAHSILAT_GIRILDI: 'Tahsilat ve finans', BORC_HATIRLATMA: 'Tahsilat ve finans', CEK_VADE: 'Tahsilat ve finans',
  CEK_KARSILIKSIZ: 'Tahsilat ve finans', KREDI_TAKSIT: 'Tahsilat ve finans', RESMI_BELGE: 'Tahsilat ve finans',
  STOK_ESIK: 'Stok ve üretim', FIYAT_ARTIS: 'Stok ve üretim', FASON_BEKLEME: 'Stok ve üretim',
  HATIRLATMA: 'Sistem', DONEM: 'Sistem', DESTEK: 'Sistem', SISTEM: 'Sistem',
};
const Anahtar = ({ acik, ad, degis }: { acik: boolean; ad: string; degis: (v: boolean) => void }) => (
  <button type="button" role="switch" aria-checked={acik} aria-label={ad} className={`ccd-switch${acik ? ' ccd-is-on' : ''}`} onClick={() => degis(!acik)} />
);

function KodKutusu({ kod }: { kod: Kod }) {
  return (
    <div className="ccd-banner">
      <span className="ccd-banner-icon" aria-hidden="true">#</span>
      <div className="ccd-stack-sm">
        <div className="ccd-mono" style={{ fontSize: 18, fontWeight: 700 }}>{kod.komut}</div>
        <div className="ccd-meta">
          {kod.botVar
            ? <>Bu satırı Telegram'da bota gönderin. Kod {kod.dakika} dakika geçerlidir ve bir kez kullanılır.</>
            : <>Bot henüz kurulmadı; kod üretildi ama bot açılınca kullanılabilir (süre {kod.dakika} dakika).</>}
        </div>
        {kod.baglanti && <a className="ccd-btn ccd-btn-primary ccd-btn-sm" href={kod.baglanti} target="_blank" rel="noreferrer">Telegram'da aç ve bağlan</a>}
      </div>
    </div>
  );
}

export function TelegramBenim() {
  const [veri, setVeri] = useState<Durum | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [kod, setKod] = useState<Kod | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const yukle = useCallback(() => {
    api<Durum>('/api/telegram/durum').then(setVeri).catch((e) => setHata(hataMetni(e, 'Telegram durumu okunamadı')));
  }, []);
  useEffect(yukle, [yukle]);
  useEffect(() => { if (!bilgi) return; const z = setTimeout(() => setBilgi(null), 2500); return () => clearTimeout(z); }, [bilgi]);

  async function kodAl() {
    setHata(null);
    try { setKod(await api<Kod>('/api/me/telegram-kod', { method: 'POST' })); } catch (e) { setHata(hataMetni(e, 'Kod alınamadı')); }
  }
  async function kes() {
    if (!window.confirm('Telegram bağlantınız kesilsin mi? Bildirimler durur; yeniden bağlanmak için kod almanız gerekir.')) return;
    try { await api('/api/me/telegram', { method: 'DELETE' }); setKod(null); yukle(); } catch (e) { setHata(hataMetni(e, 'Bağlantı kesilemedi')); }
  }
  async function kaydet(degisen: Partial<Tercih>) {
    setHata(null); setBilgi(null);
    try {
      const r = await api<{ tercih: Tercih }>('/api/me/telegram-tercih', { method: 'PUT', body: JSON.stringify(degisen) });
      setVeri((v) => (v ? { ...v, tercih: r.tercih } : v)); setBilgi('Kaydedildi');
    } catch (e) { setHata(hataMetni(e, 'Kaydedilemedi')); }
  }

  async function sustur(kural: string, durum: 'acik' | 'bugun' | 'hic') {
    setHata(null); setBilgi(null);
    try {
      const r = await api<{ durtmeler: Durtme[] }>('/api/me/telegram-susturma', { method: 'PUT', body: JSON.stringify({ kural, durum }) });
      setVeri((v) => (v ? { ...v, durtmeler: r.durtmeler } : v)); setBilgi('Kaydedildi');
    } catch (e) { setHata(hataMetni(e, 'Kaydedilemedi')); }
  }

  if (!veri) return hata ? <Hata m={hata} /> : <div className="ccd-inline-note">Yükleniyor…</div>;
  const t = veri.tercih;
  const durtmeler = veri.durtmeler ?? [];
  return (
    <>
      {hata && <Hata m={hata} />}
      <div className="ccd-card ccd-stack-sm" id="telegram" data-fihrist="telegram-baglama">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="ccd-fieldset-title">Telegram bağlantım <Ipucu id="telegram-baglama" /></div>
          {veri.ben.bagli ? <span className="ccd-badge ccd-badge-success">bağlı</span> : <span className="ccd-badge ccd-badge-warning">bağlı değil</span>}
        </div>
        <div className="ccd-meta">
          Bağlanınca paneldeki günlük işleri Telegram'dan sorar ve yaptırırsınız; her yazma işleminde önce özet gelir,
          siz <b>Onayla</b> demeden hiçbir şey yapılmaz. Bot yalnız özel sohbette çalışır.{' '}
          <Link className="ccd-link" to="/fihrist#telegram-nedir">Kullanım rehberi</Link>
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="ccd-btn ccd-btn-sm ccd-btn-primary" onClick={kodAl} data-tur="telegram">{veri.ben.bagli ? 'Yeniden bağla (yeni kod)' : 'Telegram\'ı bağla'}</button>
          {veri.ben.bagli && <button className="ccd-btn ccd-btn-sm" onClick={kes}>Bağlantıyı kes</button>}
        </div>
        {kod && <KodKutusu kod={kod} />}
        {!veri.bot.acik && <div className="ccd-meta">Bot şu an açık değil{veri.bot.hata ? `: ${veri.bot.hata}` : ''}. Yöneticinize haber verin.</div>}
      </div>

      <div className="ccd-card ccd-stack" id="telegram-tercih" data-fihrist="telegram-tercih">
        <div>
          <div className="ccd-fieldset-title">Bildirim tercihlerim <Ipucu id="telegram-tercih" /></div>
          <div className="ccd-meta">Değişiklik anında kaydedilir. Bugün asistana yazdığınız ileti: {veri.kullanim.bugun} / {veri.kullanim.tavan}</div>
        </div>

        <section className="ccd-tercih-bolum" aria-label="Özetler ve sessiz saat">
          <div className="ccd-tercih-satir">
            <div><b>Günlük özet</b><div className="ccd-meta">Hafta içi her sabah günün işleri tek iletide.</div></div>
            <div className="ccd-tercih-denetim">
              <input className="ccd-input ccd-tercih-saat" type="time" aria-label="Günlük özet saati" value={t.saat} disabled={!t.gunluk} onChange={(e) => e.target.value && kaydet({ saat: e.target.value })} />
              <Anahtar acik={t.gunluk} ad="Günlük özet" degis={(v) => kaydet({ gunluk: v })} />
            </div>
          </div>
          <div className="ccd-tercih-satir">
            <div><b>Haftalık özet</b><div className="ccd-meta">Pazartesi sabahı geçen haftanın özeti.</div></div>
            <div className="ccd-tercih-denetim"><Anahtar acik={t.haftalik} ad="Haftalık özet" degis={(v) => kaydet({ haftalik: v })} /></div>
          </div>
          <div className="ccd-tercih-satir">
            <div><b>Sessiz saat</b><div className="ccd-meta">Bu aralıkta acil olmayan bildirim bekletilir, bitişte gönderilir.</div></div>
            <div className="ccd-tercih-denetim">
              <input className="ccd-input ccd-tercih-saat" type="time" aria-label="Sessiz saat başlangıcı" value={t.sessizBas} onChange={(e) => e.target.value && kaydet({ sessizBas: e.target.value })} />
              <span className="ccd-meta" aria-hidden="true">–</span>
              <input className="ccd-input ccd-tercih-saat" type="time" aria-label="Sessiz saat bitişi" value={t.sessizBit} onChange={(e) => e.target.value && kaydet({ sessizBit: e.target.value })} />
            </div>
          </div>
        </section>

        <div className="ccd-stack-sm">
          <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div><b>Hangi bildirimler gelsin</b> <span className="ccd-meta">· {veri.turler.length - t.kapali.filter((k) => veri.turler.some((x) => x.id === k)).length} / {veri.turler.length} açık · <span className="ccd-tercih-acil" aria-hidden="true">●</span> sessiz saatte de gelir</span></div>
            <div className="ccd-row" style={{ gap: 12 }}>
              <button type="button" className="ccd-borc-bag" onClick={() => kaydet({ kapali: t.kapali.filter((k) => !veri.turler.some((x) => x.id === k)) })}>tümünü aç</button>
              <button type="button" className="ccd-borc-bag" onClick={() => kaydet({ kapali: [...new Set([...t.kapali, ...veri.turler.map((x) => x.id)])] })}>tümünü kapat</button>
            </div>
          </div>
          <div className="ccd-tercih-gruplar">
            {TUR_GRUPLARI.map((g) => {
              const liste = veri.turler.filter((x) => (TUR_GRUBU[x.id] ?? 'Diğer') === g);
              if (liste.length === 0) return null;
              return (
                <section key={g} className="ccd-tercih-grup" aria-label={g}>
                  <div className="ccd-eyebrow">{g}</div>
                  {liste.map((tur) => (
                    <div key={tur.id} className="ccd-tercih-tur">
                      <span>{tur.ad}{tur.acil && <span className="ccd-tercih-acil" title="Acil: sessiz saatte de gelir" aria-label="sessiz saatte de gelir"> ●</span>}</span>
                      <Anahtar acik={!t.kapali.includes(tur.id)} ad={tur.ad}
                               degis={(v) => kaydet({ kapali: v ? t.kapali.filter((x) => x !== tur.id) : [...t.kapali, tur.id] })} />
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        </div>
      </div>

      {durtmeler.length > 0 && (
        <div className="ccd-card ccd-stack-sm" id="telegram-durtme">
          <div>
            <div className="ccd-fieldset-title">Hatırlatma dürtmeleri</div>
            <div className="ccd-meta">Bekleyen bir iş olduğunda bot size sorar; aynı konu için günde en çok bir kez. Seçim yalnız sizin için geçerlidir.</div>
          </div>
          <section className="ccd-tercih-bolum" aria-label="Hatırlatma dürtmeleri">
            {durtmeler.map((d) => (
              <div key={d.kod} className="ccd-tercih-satir">
                <div>
                  <b>{d.ad}</b>{d.esik !== null && <span className="ccd-meta"> · {d.esik} {d.birim ? (BIRIM_AD[d.birim] ?? d.birim) : ''}</span>}
                  <div className="ccd-meta">{d.aciklama}</div>
                </div>
                <div className="ccd-secmeli" role="radiogroup" aria-label={d.ad}>
                  {([['acik', 'Açık'], ['bugun', 'Bugün sorma'], ['hic', 'Hiç sorma']] as const).map(([durum, etiket]) => (
                    <button key={durum} type="button" role="radio" aria-checked={(d.susturma ?? 'acik') === durum} onClick={() => (d.susturma ?? 'acik') !== durum && sustur(d.kod, durum)}>{etiket}</button>
                  ))}
                </div>
              </div>
            ))}
          </section>
        </div>
      )}
      {bilgi && <div className="ccd-tost" role="status"><span aria-hidden="true">✓</span> {bilgi}</div>}
    </>
  );
}

/** Sağlık: bot süreci, kuyruk, devre kesici, bugünkü sayaçlar. 30 saniyede bir kendiliğinden yenilenir. */
function SaglikKutusu() {
  const [veri, setVeri] = useState<Saglik | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const yukle = useCallback(() => {
    setYukleniyor(true);
    api<Saglik>('/api/telegram/saglik').then((v) => { setVeri(v); setHata(null); })
      .catch((e) => setHata(hataMetni(e, 'Sağlık bilgisi okunamadı'))).finally(() => setYukleniyor(false));
  }, []);
  useEffect(() => {
    yukle();
    const t = window.setInterval(yukle, 30_000);
    return () => window.clearInterval(t);
  }, [yukle]);

  const b = veri?.bugun;
  return (
    <div className="ccd-card ccd-stack-sm" id="telegram-saglik">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-fieldset-title">Sağlık</div>
        <div className="ccd-row" style={{ gap: 8 }}>
          <span className="ccd-meta">30 saniyede bir yenilenir</span>
          <button className="ccd-btn ccd-btn-sm" onClick={yukle} disabled={yukleniyor}>{yukleniyor ? 'Yenileniyor…' : 'Yenile'}</button>
        </div>
      </div>
      {hata && <Hata m={hata} />}
      {!veri && !hata && <div className="ccd-inline-note">Yükleniyor…</div>}
      {veri && b && (
        <>
          {veri.uyarilar.map((u) => (
            <div key={u} className="ccd-banner ccd-banner-warning"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{u}</span></div>
          ))}
          <div className="ccd-row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <span>Bot süreci: {veri.sahip
              ? <span className={`ccd-badge ${veri.sahip.canli && !veri.kalpGecikti ? 'ccd-badge-success' : 'ccd-badge-danger'}`} title={`Süreç: ${veri.sahip.surec} · devir: ${veri.sahip.devir}`}>
                  {veri.sahip.canli && !veri.kalpGecikti ? 'çalışıyor' : 'yanıt vermiyor'} · son kalp atışı {once(veri.sahip.kalpSn)}
                </span>
              : <span className="ccd-badge ccd-badge-warning">botu çalıştıran süreç yok</span>}</span>
            <span>Devre kesici: <span className={`ccd-badge ${veri.kesici.acik ? 'ccd-badge-danger' : 'ccd-badge-success'}`}>{veri.kesici.acik ? 'açık (asistan bekletiliyor)' : 'kapalı'}</span>
              <span className="ccd-meta"> · bugün {veri.kesici.bugunAcilma} kez açıldı</span></span>
          </div>
          <div className="ccd-meta">
            {veri.sahip && <>Süreç: <span className="ccd-mono">{veri.sahip.surec}</span> · </>}
            Son işlenen güncelleme: {veri.sonGuncellemeAt ? formatTarih(veri.sonGuncellemeAt) : 'henüz yok'} · Bugün gelen güncelleme: {sayi(b.guncelleme)}
          </div>
          {veri.bot.hata && <div className="ccd-meta">Son bot hatası: {veri.bot.hata}</div>}
          <div className="ccd-table-wrap">
            <table className="ccd-table">
              <tbody>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Giden kuyruk</b></td>
                  <td className="ccd-td">
                    Bekleyen: {sayi(veri.kuyruk.bekleyen)} (uyarı eşiği {sayi(veri.kuyruk.esik)}) · Gönderiliyor: {sayi(veri.kuyruk.gonderiliyor)} ·
                    En eski bekleyen: {veri.kuyruk.enEskiSn === null ? 'yok' : `${sayi(Math.round(veri.kuyruk.enEskiSn / 60))} dk`} ·
                    Ertelenen (sessiz saat / tavan): {sayi(veri.kuyruk.ertelenen)}
                  </td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Son 24 saat</b></td>
                  <td className="ccd-td">
                    Giden: {sayi(veri.kuyruk.giden24)} · Hata: {sayi(veri.kuyruk.hata24)} · İptal: {sayi(veri.kuyruk.iptal24)} ·
                    Sonucu belirsiz işlem: {sayi(veri.islem.belirsiz24)} · Yapılamayan işlem: {sayi(veri.islem.hata24)} · Şu an çalışan işlem: {sayi(veri.islem.calisan)}
                  </td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Bugün: işlemler</b></td>
                  <td className="ccd-td">
                    Önerilen: {sayi(b.onerilen)} · Onaylanan: {sayi(b.onaylanan)} · Vazgeçilen: {sayi(b.vazgecilen)} · Süresi dolan: {sayi(b.suresiDolan)} ·
                    Başarısız: {sayi(b.basarisiz)} · Geri alma: {sayi(b.geriAlma)} · Belirsiz: {sayi(b.belirsiz)}
                  </td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Bugün: asistan</b></td>
                  <td className="ccd-td">
                    Asistan turu: {sayi(b.asistanTuru)} · Araç çağrısı: {sayi(b.aracCagrisi)} · Asistan hatası: {sayi(b.asistanHata)} ·
                    Takılma: {sayi(b.takilma)} · Reddedilen dosya: {sayi(b.dosyaRed)}
                  </td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Bugün: giden ileti</b></td>
                  <td className="ccd-td">
                    Gitti: {sayi(b.gidenGitti)} · Hata: {sayi(b.gidenHata)} · Hız sınırı (429): {sayi(b.giden429)} ·
                    Botu engelleyen: {sayi(b.engelleyen)} · Kuru çalıştırmada bekletilen: {sayi(b.kuru)}
                  </td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Gecikme (bugün)</b></td>
                  <td className="ccd-td">Asistan sağlayıcısı: {gecikme(b.saglayiciP50, b.saglayiciP95)} · Panel işlemi: {gecikme(b.islemP50, b.islemP95)}</td>
                </tr>
                <tr className="ccd-tr">
                  <td className="ccd-td"><b>Kullanım göstergesi (bugün)</b></td>
                  <td className="ccd-td">
                    Asistan turu: {sayi(veri.maliyet.asistanTuru)} · Girdi token: {sayi(veri.maliyet.tokenGirdi)} · Çıktı token: {sayi(veri.maliyet.tokenCikti)}
                    <div className="ccd-meta">Para tutarı gösterilmez; sağlayıcı panelinden izleyin.</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="ccd-fieldset-title">Ulaşılamayan kişiler</div>
          {veri.ulasilamayan.length === 0 ? <div className="ccd-meta">Ulaşılamayan kişi yok.</div> : (
            <>
              <ul className="ccd-meta" style={{ margin: 0, paddingLeft: 18 }}>
                {veri.ulasilamayan.map((k) => <li key={k.id}><b>{k.ad}</b> — {formatTarih(k.at)} tarihinden beri</li>)}
              </ul>
              <div className="ccd-meta">Botu engellemiş ya da sohbeti silmiş; otomatik ileti gönderilmiyor. Kişi bota yeniden yazınca kendiliğinden açılır.</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Otomasyon kuralları: aç / kapa, eşik ve (dürtmelerde) iş saati. Alıcı, tür ve birim panelden değişmez. */
function KurallarKutusu({ yazar }: { yazar: boolean }) {
  const [kurallar, setKurallar] = useState<Kural[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [taslak, setTaslak] = useState<Record<string, { esik?: string; isBas?: string; isBit?: string }>>({});
  const [bekleyen, setBekleyen] = useState<string | null>(null);
  const yukle = useCallback(() => {
    api<{ kurallar: Kural[] }>('/api/telegram/kurallar').then((r) => setKurallar(r.kurallar)).catch((e) => setHata(hataMetni(e, 'Otomasyon kuralları okunamadı')));
  }, []);
  useEffect(yukle, [yukle]);

  async function yaz(kod: string, govde: { acik?: boolean; esik?: number; isBas?: string; isBit?: string }) {
    setHata(null); setBekleyen(kod);
    try {
      await api(`/api/telegram/kurallar/${encodeURIComponent(kod)}`, { method: 'PUT', body: JSON.stringify(govde) });
      setTaslak((t) => { const { [kod]: _silinen, ...kalan } = t; return kalan; });
      yukle();
    } catch (e) { setHata(hataMetni(e, 'Kural kaydedilemedi')); } finally { setBekleyen(null); }
  }
  const degis = (kod: string, alan: 'esik' | 'isBas' | 'isBit', deger: string) => setTaslak((t) => ({ ...t, [kod]: { ...t[kod], [alan]: deger } }));
  const neden = yazar ? undefined : 'Değiştirme yetkiniz yok';

  function satir(k: Kural) {
    const t = taslak[k.kod] ?? {};
    const esikMetin = t.esik ?? (k.esik === null ? '' : String(k.esik));
    const isBas = t.isBas ?? k.isBas; const isBit = t.isBit ?? k.isBit;
    const esikSayi = Number(esikMetin);
    const esikGecerli = k.esik === null || (esikMetin.trim() !== '' && Number.isInteger(esikSayi));
    const saatGecerli = k.tur !== 'DURTME' || (isBas !== '' && isBit !== '');
    const esikDegisti = k.esik !== null && esikGecerli && esikSayi !== k.esik;
    const saatDegisti = k.tur === 'DURTME' && (isBas !== k.isBas || isBit !== k.isBit);
    const degisti = t.esik !== undefined || t.isBas !== undefined || t.isBit !== undefined;
    const kaydedilebilir = (esikDegisti || saatDegisti) && esikGecerli && saatGecerli;
    const mesgul = bekleyen === k.kod;
    return (
      <tr key={k.kod} className="ccd-tr">
        <td className="ccd-td"><b>{k.ad}</b><div className="ccd-meta">{k.aciklama}</div></td>
        <td className="ccd-td">
          <button type="button" role="switch" aria-checked={k.acik} aria-label={`${k.ad}: açık / kapalı`}
                  className={`ccd-switch${k.acik ? ' ccd-is-on' : ''}`} disabled={!yazar || mesgul}
                  title={neden ?? (k.acik ? 'Kapat' : 'Aç')} onClick={() => void yaz(k.kod, { acik: !k.acik })} />
        </td>
        <td className="ccd-td">
          {k.esik === null ? '—' : (
            <span className="ccd-row" style={{ gap: 6 }}>
              <input className={`ccd-input${esikGecerli ? '' : ' ccd-input-invalid'}`} type="number" step={1} value={esikMetin} disabled={!yazar || mesgul} title={neden}
                     aria-label={`${k.ad}: eşik`} onChange={(e) => degis(k.kod, 'esik', e.target.value)} style={{ width: 90 }} />
              <span className="ccd-meta">{k.birim ? BIRIM_AD[k.birim] : ''}</span>
            </span>
          )}
        </td>
        <td className="ccd-td">
          {k.tur !== 'DURTME' ? '—' : (
            <span className="ccd-row" style={{ gap: 6 }}>
              <input className="ccd-input" type="time" value={isBas} disabled={!yazar || mesgul} title={neden} aria-label={`${k.ad}: iş saati başlangıcı`}
                     onChange={(e) => degis(k.kod, 'isBas', e.target.value)} style={{ width: 110 }} />
              <span className="ccd-meta">–</span>
              <input className="ccd-input" type="time" value={isBit} disabled={!yazar || mesgul} title={neden} aria-label={`${k.ad}: iş saati bitişi`}
                     onChange={(e) => degis(k.kod, 'isBit', e.target.value)} style={{ width: 110 }} />
            </span>
          )}
        </td>
        <td className="ccd-td">{k.alici === 'SAHIP' ? 'Kaydın sahibi' : (k.alici || '—')}</td>
        <td className="ccd-td">{k.sonTetikAt ? formatTarih(k.sonTetikAt) : '—'}</td>
        <td className="ccd-td ccd-td-num">{sayi(k.bugun)}</td>
        <td className="ccd-td">
          {degisti && (
            <span className="ccd-row" style={{ gap: 6 }}>
              <button className="ccd-btn ccd-btn-sm ccd-btn-primary" disabled={!kaydedilebilir || mesgul}
                      title={!esikGecerli ? 'Eşik tam sayı olmalı' : !saatGecerli ? 'İş saati başlangıcı ve bitişi gerekli' : !kaydedilebilir ? 'Değişiklik yok' : undefined}
                      onClick={() => void yaz(k.kod, { ...(esikDegisti ? { esik: esikSayi } : {}), ...(saatDegisti ? { isBas, isBit } : {}) })}>
                {mesgul ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
              <button className="ccd-btn ccd-btn-sm" disabled={mesgul} onClick={() => setTaslak((x) => { const { [k.kod]: _silinen, ...kalan } = x; return kalan; })}>Vazgeç</button>
            </span>
          )}
        </td>
      </tr>
    );
  }
  const tablo = (liste: Kural[]) => (
    <div className="ccd-table-wrap">
      <table className="ccd-table">
        <thead><tr>
          <th className="ccd-th">Kural</th><th className="ccd-th">Açık</th><th className="ccd-th">Eşik</th><th className="ccd-th">İş saati</th>
          <th className="ccd-th">Alıcı</th><th className="ccd-th">Son tetiklenme</th><th className="ccd-th ccd-td-num">Bugün</th><th className="ccd-th" />
        </tr></thead>
        <tbody>{liste.map(satir)}</tbody>
      </table>
    </div>
  );

  return (
    <div className="ccd-card ccd-stack-sm" id="telegram-kurallar">
      <div className="ccd-fieldset-title">Otomasyon kuralları</div>
      <div className="ccd-meta">
        Dürtmeler yalnız kuralın iş saatinde, aynı konu için kişi başına günde en çok bir kez gider. Kural açıldığı andan önceki kayıtlar için ileti gönderilmez.
        Kişi kendi dürtmesini Telegram'daki 'Bugün sorma / Hiç sorma' düğmesiyle ya da 'Telegram'ım' sayfasından kapatabilir.
      </div>
      {!yazar && <div className="ccd-meta">Kuralları yalnız ayarları değiştirebilen rol düzenler; bu tablo sizin için salt okunurdur.</div>}
      {hata && <Hata m={hata} />}
      {!kurallar && !hata && <div className="ccd-inline-note">Yükleniyor…</div>}
      {kurallar && kurallar.length === 0 && <div className="ccd-meta">Tanımlı otomasyon kuralı yok.</div>}
      {kurallar && KURAL_GRUPLARI.map((g) => {
        const liste = kurallar.filter((k) => k.tur === g.tur);
        if (liste.length === 0) return null;
        return g.kapali ? (
          <details key={g.tur}>
            <summary className="ccd-fieldset-title" style={{ cursor: 'pointer' }}>{g.ad} ({liste.length} kural, {liste.filter((k) => k.acik).length} açık)</summary>
            {tablo(liste)}
          </details>
        ) : (
          <Fragment key={g.tur}><div className="ccd-fieldset-title">{g.ad}</div>{tablo(liste)}</Fragment>
        );
      })}
    </div>
  );
}

/** Kuru çalıştırmada GÖNDERİLMEYEN iletiler (son 3 gün). `surum` değişince yeniden okunur. */
function KuruListesi({ surum }: { surum: number }) {
  const [satirlar, setSatirlar] = useState<KuruSatir[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    api<{ satirlar: KuruSatir[] }>('/api/telegram/kuru').then((r) => { setSatirlar(r.satirlar); setHata(null); })
      .catch((e) => setHata(hataMetni(e, 'Kuru çalıştırma listesi okunamadı')));
  }, [surum]);
  if (hata) return <Hata m={hata} />;
  if (!satirlar) return <div className="ccd-inline-note">Yükleniyor…</div>;
  if (satirlar.length === 0) return <div className="ccd-meta">Son 3 günde kuru çalıştırmada bekletilen ileti yok.</div>;
  return (
    <>
      <div className="ccd-table-wrap" style={{ maxHeight: 280, overflow: 'auto' }}>
        <table className="ccd-table">
          <thead><tr><th className="ccd-th">Kime</th><th className="ccd-th">Tür</th><th className="ccd-th">Ne zaman</th></tr></thead>
          <tbody>
            {satirlar.map((s) => (
              <tr key={s.id} className="ccd-tr">
                <td className="ccd-td">{s.ad}</td>
                <td className="ccd-td">{s.tur} <span className="ccd-meta">· {s.sinif}</span></td>
                <td className="ccd-td ccd-mono">{formatTarih(s.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ccd-meta">İleti içeriği gösterilmez: içerik alıcının rolüne göre süzülmüştü. Son 3 gün, en çok 200 satır.</div>
    </>
  );
}

function TakilmaKutusu() {
  const [veri, setVeri] = useState<Takilma | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    api<Takilma>('/api/telegram/takilma').then(setVeri).catch((e) => setHata(hataMetni(e, 'Takılınan konular okunamadı')));
  }, []);
  return (
    <div className="ccd-card ccd-stack-sm" id="telegram-takilma">
      <div className="ccd-fieldset-title">En çok takılınan konular</div>
      <div className="ccd-meta">Asistanın anlayamadığı ya da yapamadığı isteklerin yalnız kaba konu etiketi sayılır; ileti metni saklanmaz. Son 30 gün.</div>
      {hata && <Hata m={hata} />}
      {!veri && !hata && <div className="ccd-inline-note">Yükleniyor…</div>}
      {veri && veri.konular.length === 0 && <div className="ccd-meta">Son 30 günde takılınan konu kaydı yok.</div>}
      {veri && veri.konular.length > 0 && (
        <div className="ccd-table-wrap" style={{ maxHeight: 340, overflow: 'auto' }}>
          <table className="ccd-table">
            <thead><tr><th className="ccd-th">Konu</th><th className="ccd-th ccd-td-num">Adet</th><th className="ccd-th">Son</th><th className="ccd-th">Kimler</th></tr></thead>
            <tbody>
              {veri.konular.map((k) => (
                <tr key={k.konu} className="ccd-tr">
                  <td className="ccd-td"><b>{k.konu}</b></td>
                  <td className="ccd-td ccd-td-num">{sayi(k.adet)}</td>
                  <td className="ccd-td ccd-mono">{formatTarih(k.son)}</td>
                  <td className="ccd-td">{k.kisiler.map((x) => `${x.ad} × ${x.adet}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TelegramYonetim() {
  const { can } = useAuth();
  const [veri, setVeri] = useState<Yonetim | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [kod, setKod] = useState<{ kim: string; kod: Kod } | null>(null);
  const [kuruSurum, setKuruSurum] = useState(0);
  const yukle = useCallback(() => {
    api<Yonetim>('/api/telegram/yonetim').then(setVeri).catch((e) => setHata(hataMetni(e, 'Telegram yönetim bilgisi okunamadı')));
  }, []);
  useEffect(yukle, [yukle]);

  async function bayrak(ad: 'asistan' | 'yazma' | 'otomatik' | 'kuru', deger: boolean) {
    const soru = { asistan: 'Yazıyla konuşan asistan', yazma: 'Telegram\'dan yazma işlemleri', otomatik: 'Otomatik iletiler (bildirim ve özet)', kuru: 'Kuru çalıştırma (otomatik iletiler gönderilmez)' }[ad];
    if (!window.confirm(`${soru} ${deger ? 'AÇILSIN' : 'KAPATILSIN'} mı?`)) return;
    setHata(null);
    try { await api('/api/telegram/bayrak', { method: 'PUT', body: JSON.stringify({ [ad]: deger }) }); yukle(); setKuruSurum((n) => n + 1); } catch (e) { setHata(hataMetni(e, 'Değiştirilemedi')); }
  }
  async function kodUret(id: number, kim: string) {
    try { setKod({ kim, kod: await api<Kod>(`/api/kullanicilar/${id}/telegram-kod`, { method: 'POST' }) }); } catch (e) { setHata(hataMetni(e, 'Kod üretilemedi')); }
  }
  async function kes(id: number, kim: string) {
    if (!window.confirm(`${kim} için Telegram bağlantısı kesilsin mi?`)) return;
    try { await api(`/api/kullanicilar/${id}/telegram`, { method: 'DELETE' }); yukle(); } catch (e) { setHata(hataMetni(e, 'Bağlantı kesilemedi')); }
  }

  if (!veri) return hata ? <Hata m={hata} /> : <div className="ccd-inline-note">Yükleniyor…</div>;
  const yonetir = can('user.manage'); const yazar = can('settings.write');
  const rozet = (acik: boolean, evet: string, hayir: string) => <span className={`ccd-badge ${acik ? 'ccd-badge-success' : 'ccd-badge-warning'}`}>{acik ? evet : hayir}</span>;
  return (
    <>
      {hata && <Hata m={hata} />}
      <SaglikKutusu />
      <div className="ccd-card ccd-stack-sm" id="telegram-durum">
        <div className="ccd-fieldset-title">Bot ve asistan durumu</div>
        <div className="ccd-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span>Bot: {rozet(veri.bot.acik, veri.bot.kullaniciAdi ? `açık · @${veri.bot.kullaniciAdi}` : 'açık', veri.bot.tokenVar ? 'kapalı' : 'kurulmadı')}</span>
          <span>Asistan anahtarı: {rozet(veri.asistan.kurulu, 'var', 'yok')}</span>
          <span className="ccd-meta">Bekleyen (sessiz saat) bildirim: {veri.kuyruk} · Günlük ileti tavanı: {veri.tavan.gunlukIleti} · Tek iletide araç turu: {veri.tavan.aracTuru}</span>
        </div>
        {veri.bot.hata && <div className="ccd-meta">Son hata: {veri.bot.hata}</div>}
        {!veri.asistan.kurulu && <div className="ccd-meta">Asistan anahtarı yokken bot menü, komut, düğme ve bildirimlerle çalışır; yalnız düz yazıyla konuşma kapalıdır.</div>}
        <div className="ccd-meta">Sorun olursa aşağıdaki anahtarlarla (yeniden kurulum gerekmeden) kapatabilirsiniz:</div>
        <div className="ccd-row" style={{ gap: 16, flexWrap: 'wrap' }}>
          {([['asistan', 'Yazıyla konuşan asistan'], ['yazma', 'Yazma işlemleri (kapalıyken yalnız okuma)'], ['otomatik', 'Otomatik iletiler (bildirim, özet)']] as const).map(([ad, etiket]) => (
            <label key={ad} className="ccd-row" style={{ gap: 6 }}>
              <input type="checkbox" checked={veri.bayrak[ad]} disabled={!yazar} title={yazar ? undefined : 'Değiştirme yetkiniz yok'} onChange={(e) => bayrak(ad, e.target.checked)} /> {etiket}
            </label>
          ))}
        </div>
        <div className="ccd-stack-sm" id="telegram-kuru">
          <label className="ccd-row" style={{ gap: 6 }}>
            <input type="checkbox" checked={veri.bayrak.kuru} disabled={!yazar} title={yazar ? undefined : 'Değiştirme yetkiniz yok'} onChange={(e) => bayrak('kuru', e.target.checked)} />
            Kuru çalıştırma (otomatik iletiler GÖNDERİLMEZ)
            {veri.bayrak.kuru && <span className="ccd-badge ccd-badge-warning">açık</span>}
          </label>
          <div className="ccd-meta">
            Açıkken uyarı, özet ve dürtmeler gönderilmez; ne gidecekti listesi aşağıda görünür ve hafta içi 18:00'den sonra yöneticilere günde tek ileti olarak gider.
            Elle sorulan şeyler ve onay akışı etkilenmez.
          </div>
          <KuruListesi surum={kuruSurum} />
        </div>
      </div>

      <KurallarKutusu yazar={yazar} />

      <div className="ccd-card ccd-stack-sm" id="telegram-kullanicilar">
        <div className="ccd-fieldset-title">Kim bağlı</div>
        {kod && <><div className="ccd-meta">{kod.kim} için bağlantı kodu (kişiye siz iletin):</div><KodKutusu kod={kod.kod} /></>}
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Kişi</th><th className="ccd-th">Rol</th><th className="ccd-th">Telegram</th>
              <th className="ccd-th ccd-td-num">Bugün ileti</th><th className="ccd-th ccd-td-num">7 gün: ileti</th><th className="ccd-th ccd-td-num">Yapılan işlem</th>
              <th className="ccd-th ccd-td-num">Yapılamayan</th><th className="ccd-th ccd-td-num">Vazgeçilen</th><th className="ccd-th" />
            </tr></thead>
            <tbody>
              {veri.kullanicilar.map((k) => (
                <tr key={k.id} className="ccd-tr">
                  <td className="ccd-td"><b>{k.name}</b>{!k.active && <span className="ccd-meta"> · pasif</span>}</td>
                  <td className="ccd-td">{k.roleLabel}</td>
                  <td className="ccd-td">{k.bagli ? <span className="ccd-badge ccd-badge-success">bağlı</span> : <span className="ccd-muted">bağlı değil</span>}
                    {k.ulasilamiyor && <> <span className="ccd-badge ccd-badge-warning" title="Botu engellemiş ya da sohbeti silmiş; otomatik ileti gönderilmiyor">ulaşılamıyor</span></>}</td>
                  <td className="ccd-td ccd-td-num">{k.bugunIleti}</td><td className="ccd-td ccd-td-num">{k.yediGun.ileti}</td>
                  <td className="ccd-td ccd-td-num">{k.yediGun.yapilan}</td><td className="ccd-td ccd-td-num">{k.yediGun.hata}</td><td className="ccd-td ccd-td-num">{k.yediGun.vazgecilen}</td>
                  <td className="ccd-td">
                    {yonetir && k.active && <button className="ccd-btn ccd-btn-sm" onClick={() => kodUret(k.id, k.name)}>Kod üret</button>}{' '}
                    {yonetir && k.bagli && <button className="ccd-btn ccd-btn-sm" onClick={() => kes(k.id, k.name)}>Bağlantıyı kes</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TakilmaKutusu />

      <div className="ccd-card ccd-stack-sm" id="telegram-matris" data-fihrist="telegram-yapilamayanlar">
        <div className="ccd-fieldset-title">Telegram'dan neler yapılabilir <Ipucu id="telegram-onay" /></div>
        <div className="ccd-meta">
          Listede olmayan işlem Telegram'dan yapılamaz. Her işlem panelin kendi yetki kurallarından geçer ve onay düğmesi ister.
          Okunabilen ekran sayısı: {veri.matris.okumaAdedi}. Bu tablo salt okunurdur.
        </div>
        <div className="ccd-table-wrap" style={{ maxHeight: 340, overflow: 'auto' }}>
          <table className="ccd-table">
            <thead><tr><th className="ccd-th">İşlem</th><th className="ccd-th">Onay</th></tr></thead>
            <tbody>
              {veri.matris.yazma.map((m) => (
                <tr key={`${m.metot} ${m.yol}`} className="ccd-tr" title={`${m.metot} ${m.yol}`}>
                  <td className="ccd-td">{m.ad}</td>
                  <td className="ccd-td">{m.sinif === 'GERI_ALINAMAZ' ? <span className="ccd-badge ccd-badge-danger">geri alınamaz · iki adım</span> : <span className="ccd-badge ccd-badge-info">özet + onay</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ccd-fieldset-title">Yalnız panelde kalanlar <Ipucu id="telegram-yapilamayanlar" /></div>
        <ul className="ccd-meta" style={{ margin: 0, paddingLeft: 18 }}>
          {veri.matris.yasak.map((y) => <li key={y.ad}><b>{y.ad}</b> — {y.neden}</li>)}
        </ul>
      </div>

      <div className="ccd-card ccd-stack-sm" id="telegram-islemler" data-fihrist="telegram-geri-alma">
        <div className="ccd-fieldset-title">Son 50 Telegram işlemi <Ipucu id="telegram-geri-alma" /></div>
        {veri.islemler.length === 0 ? <div className="ccd-meta">Henüz Telegram'dan işlem yapılmadı.</div> : (
          <div className="ccd-table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
            <table className="ccd-table">
              <thead><tr><th className="ccd-th">Zaman</th><th className="ccd-th">Kişi</th><th className="ccd-th">İşlem</th><th className="ccd-th">Sonuç</th></tr></thead>
              <tbody>
                {veri.islemler.map((i) => (
                  <tr key={i.id} className="ccd-tr">
                    <td className="ccd-td ccd-mono">{formatTarih(i.at)}</td><td className="ccd-td">{i.kullanici}</td>
                    <td className="ccd-td">{i.ad ?? i.action}</td>
                    <td className="ccd-td">
                      {i.durum === 'TAMAM' && <span className="ccd-badge ccd-badge-success">yapıldı</span>}
                      {i.durum === 'HATA' && <span className="ccd-badge ccd-badge-danger" title={i.hata ?? ''}>yapılamadı</span>}
                      {i.durum === 'VAZGECILDI' && <span className="ccd-badge ccd-badge-warning">vazgeçildi</span>}
                      {i.geriAlma && <> <span className="ccd-badge ccd-badge-accent">geri alma</span></>}
                      {i.geriAlindi && <> <span className="ccd-badge ccd-badge-accent">geri alındı</span></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ccd-meta">Ayrıntı: <Link className="ccd-link" to="/kayit-izi">Kayıt izi</Link> (Telegram'dan gelen işlemler kanal bilgisiyle kaydedilir).</div>
      </div>
    </>
  );
}

/** Herkese açık kişisel sayfa: `/telegram`. */
export default function TelegramSayfasi() {
  return (
    <>
      <div>
        <h1 className="ccd-title">Telegram'ım</h1>
        <div className="ccd-meta">Telegram hesabınızı panele bağlayın, bildirimlerinizi seçin.</div>
      </div>
      <TelegramBenim />
    </>
  );
}
